import { useState, useEffect } from "react";
import { Search, Shield, ShieldOff, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export default function AdminUsers() {
  const { api } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDetail, setShowDetail] = useState(false);

  const fetchUsers = (q = "") => {
    setLoading(true);
    const params = q ? `?search=${encodeURIComponent(q)}` : "";
    api.get(`/users${params}`)
      .then((r) => setUsers(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchUsers(search);
  };

  const toggleAdmin = async (userId, currentRole) => {
    const newRole = currentRole === "admin" ? "member" : "admin";
    try {
      await api.put(`/users/${userId}/role`, { role: newRole });
      toast.success(`User role updated to ${newRole}`);
      fetchUsers(search);
    } catch (e) {
      toast.error("Failed to update role");
    }
  };

  const viewUser = async (userId) => {
    try {
      const r = await api.get(`/users/${userId}`);
      setSelectedUser(r.data);
      setShowDetail(true);
    } catch { /* ignore */ }
  };

  return (
    <div data-testid="admin-users-page">
      <div className="mb-8">
        <p className="font-mono text-xs tracking-[0.25em] uppercase text-slate-400 mb-2">Management</p>
        <h1 className="font-serif text-3xl font-bold text-slate-900 tracking-tight">Users</h1>
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="mb-6">
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search users..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="admin-user-search"
          />
        </div>
      </form>

      {loading ? (
        <div className="flex justify-center py-20"><div className="spinner"></div></div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-100">
                <th className="text-left px-5 py-3 font-mono text-xs tracking-wider uppercase text-slate-400">User</th>
                <th className="text-left px-5 py-3 font-mono text-xs tracking-wider uppercase text-slate-400">Email</th>
                <th className="text-left px-5 py-3 font-mono text-xs tracking-wider uppercase text-slate-400">Role</th>
                <th className="text-left px-5 py-3 font-mono text-xs tracking-wider uppercase text-slate-400">Joined</th>
                <th className="text-right px-5 py-3 font-mono text-xs tracking-wider uppercase text-slate-400">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.user_id} className="border-b border-slate-50 hover:bg-slate-50 transition-colors" data-testid={`admin-user-row-${u.user_id}`}>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-600 to-cyan-400 flex items-center justify-center text-white text-xs font-bold overflow-hidden">
                        {u.avatar ? <img src={u.avatar} alt="" className="w-full h-full object-cover" /> : u.name?.[0]?.toUpperCase()}
                      </div>
                      <span className="font-sans text-sm text-slate-900 font-medium">{u.name}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3 font-mono text-sm text-slate-500">{u.email}</td>
                  <td className="px-5 py-3">
                    <Badge variant={u.role === "admin" ? "default" : "outline"} className="text-xs capitalize">
                      {u.role}
                    </Badge>
                  </td>
                  <td className="px-5 py-3 font-mono text-xs text-slate-400">{new Date(u.created_at).toLocaleDateString()}</td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="sm" onClick={() => viewUser(u.user_id)} className="text-slate-400 hover:text-blue-600" data-testid={`view-user-${u.user_id}`}>
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => toggleAdmin(u.user_id, u.role)}
                        className={u.role === "admin" ? "text-amber-500 hover:text-amber-600" : "text-slate-400 hover:text-blue-600"}
                        data-testid={`toggle-admin-${u.user_id}`}
                      >
                        {u.role === "admin" ? <ShieldOff className="w-4 h-4" /> : <Shield className="w-4 h-4" />}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {users.length === 0 && (
            <div className="text-center py-10"><p className="font-sans text-sm text-slate-400">No users found</p></div>
          )}
        </div>
      )}

      {/* User Detail Dialog */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="max-w-md" data-testid="user-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">User Details</DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4 mt-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-gradient-to-r from-blue-600 to-cyan-400 flex items-center justify-center text-white text-lg font-bold overflow-hidden">
                  {selectedUser.avatar ? <img src={selectedUser.avatar} alt="" className="w-full h-full object-cover" /> : selectedUser.name?.[0]?.toUpperCase()}
                </div>
                <div>
                  <p className="font-sans text-lg font-medium text-slate-900">{selectedUser.name}</p>
                  <p className="font-mono text-sm text-slate-500">{selectedUser.email}</p>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="font-sans text-slate-500">Role</span>
                  <Badge variant="outline" className="capitalize">{selectedUser.role}</Badge>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="font-sans text-slate-500">Joined</span>
                  <span className="font-mono text-slate-600">{new Date(selectedUser.created_at).toLocaleDateString()}</span>
                </div>
                {selectedUser.bio && (
                  <div>
                    <span className="font-sans text-sm text-slate-500">Bio</span>
                    <p className="font-sans text-sm text-slate-700 mt-1">{selectedUser.bio}</p>
                  </div>
                )}
              </div>
              {selectedUser.project_roles?.length > 0 && (
                <div>
                  <p className="font-mono text-xs tracking-wider uppercase text-slate-400 mb-2">Project Roles</p>
                  {selectedUser.project_roles.map((pr, i) => (
                    <div key={i} className="flex items-center justify-between py-1.5 text-sm">
                      <span className="font-sans text-slate-600">{pr.project_id}</span>
                      <Badge variant="outline" className="text-xs capitalize">{pr.role}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
